import { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Rocket, RefreshCcw, BookOpen, Search, Trash2, EyeOff, Loader2, AlertTriangle, Database, Crosshair
} from 'lucide-react';
import {
  getAutopubBoard, updateAutopubItem, publishAutopubItem, removeAutopubBm,
  deleteAutopubItem, autopubReindexCatalog, autopubCatalogStatus, autopubCatalogSearch, autopubRunWatchNow, autopubRefreshBuybox
} from '@/services/api';

const STATUS_META = {
  pending_price: { label: 'À pricer', cls: 'bg-amber-100 text-amber-700' },
  ineligible: { label: 'Non éligible', cls: 'bg-slate-200 text-slate-600' },
  published: { label: 'Publiée', cls: 'bg-emerald-100 text-emerald-700' },
  publish_error: { label: 'Erreur publication', cls: 'bg-red-100 text-red-700' },
  sold_bm: { label: 'Vendue BM', cls: 'bg-blue-100 text-blue-700' },
  sold_b2b: { label: 'Vendue B2B', cls: 'bg-purple-100 text-purple-700' },
  removed: { label: 'Retirée', cls: 'bg-slate-200 text-slate-600' },
};

const BM_STATES = { 1: 'Comme neuf (A)', 2: 'Très bon (B)', 3: 'Bon (C)', 4: 'Correct' };

const computeSim = (price, cost, regime) => {
  const p = parseFloat(price);
  const c = parseFloat(cost) || 0;
  if (!p || p <= 0) return null;
  const commission = p * 0.12;
  const tva = regime === 'normale' ? p - p / 1.2 : Math.max(0, p - c) - Math.max(0, p - c) / 1.2;
  const net = p - commission - 20 - tva - c;
  return { commission, tva, net };
};

const eur = (v) => (v === null || v === undefined || isNaN(v)) ? '—' : `${Number(v).toFixed(2)} €`;

export default function AutoPublishPage() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [dryRun, setDryRun] = useState(false);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [busyId, setBusyId] = useState(null);
  const [docOpen, setDocOpen] = useState(false);
  const [matchItem, setMatchItem] = useState(null);
  const [matchQuery, setMatchQuery] = useState('');
  const [matchResults, setMatchResults] = useState([]);
  const [indexInfo, setIndexInfo] = useState(null);
  const [reindexing, setReindexing] = useState(false);
  const [refreshingBuybox, setRefreshingBuybox] = useState(false);

  const load = useCallback(async () => {
    try {
      const [board, idx] = await Promise.all([getAutopubBoard(filter), autopubCatalogStatus()]);
      setItems(board.items);
      setCounts(board.counts);
      setDryRun(board.dry_run);
      setIndexInfo(idx);
    } catch (e) {
      toast.error('Erreur de chargement du board');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const getDraft = (it) => drafts[it.id] || {
    sale_price: it.sale_price ?? '', cost_total: it.cost_total ?? '', tva_regime: it.tva_regime || 'marge'
  };

  const setDraft = (id, patch) => setDrafts((d) => ({ ...d, [id]: { ...getDraft(items.find(i => i.id === id)), ...d[id], ...patch } }));

  const saveItem = async (it) => {
    const d = getDraft(it);
    setBusyId(it.id);
    try {
      await updateAutopubItem(it.id, {
        sale_price: d.sale_price ? parseFloat(d.sale_price) : null,
        cost_total: d.cost_total !== '' ? parseFloat(d.cost_total) : null,
        tva_regime: d.tva_regime,
      });
      toast.success('Prix enregistré');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur sauvegarde');
    } finally { setBusyId(null); }
  };

  const publish = async (it) => {
    const d = getDraft(it);
    if (!d.sale_price || parseFloat(d.sale_price) <= 0) { toast.error('Définissez un prix de vente'); return; }
    setBusyId(it.id);
    try {
      await updateAutopubItem(it.id, {
        sale_price: parseFloat(d.sale_price),
        cost_total: d.cost_total !== '' ? parseFloat(d.cost_total) : null,
        tva_regime: d.tva_regime,
      });
      const res = await publishAutopubItem(it.id);
      toast.success(res.message || 'Annonce publiée sur Back Market');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de publication');
      await load();
    } finally { setBusyId(null); }
  };

  const removeFromBm = async (it) => {
    setBusyId(it.id);
    try {
      await removeAutopubBm(it.id);
      toast.success('Annonce retirée (quantité 0)');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setBusyId(null); }
  };

  const deleteItem = async (it) => {
    if (!window.confirm(`Supprimer ${it.title} (${it.imei}) du board ?`)) return;
    setBusyId(it.id);
    try {
      await deleteAutopubItem(it.id);
      toast.success('Produit supprimé du board');
      await load();
    } catch (e) { toast.error('Erreur suppression'); }
    finally { setBusyId(null); }
  };

  const reindex = async () => {
    setReindexing(true);
    try {
      await autopubReindexCatalog();
      toast.info('Indexation du catalogue BM lancée en arrière-plan (~1 min)');
      setTimeout(async () => {
        setIndexInfo(await autopubCatalogStatus());
        setReindexing(false);
      }, 45000);
    } catch (e) { toast.error('Erreur indexation'); setReindexing(false); }
  };

  const refreshBuybox = async () => {
    setRefreshingBuybox(true);
    try {
      const res = await autopubRefreshBuybox();
      toast.success(`Prix Backbox actualisés (${res.listings} annonces actives)`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur Backbox'); }
    finally { setRefreshingBuybox(false); }
  };

  const runWatch = async () => {
    try {
      await autopubRunWatchNow();
      toast.info('Vérification des ventes BM lancée');
      setTimeout(load, 8000);
    } catch (e) { toast.error('Erreur'); }
  };

  const searchMatch = async (q) => {
    setMatchQuery(q);
    if (q.length < 3) { setMatchResults([]); return; }
    try {
      const res = await autopubCatalogSearch(q);
      setMatchResults(res.results);
    } catch (e) { /* noop */ }
  };

  const selectMatch = async (r) => {
    try {
      await updateAutopubItem(matchItem.id, { backmarket_id: r.backmarket_id, matched_title: r.title });
      toast.success(`Produit associé : ${r.title}`);
      setMatchItem(null);
      await load();
    } catch (e) { toast.error('Erreur association'); }
  };

  const statCards = [
    { key: 'pending_price', color: 'text-amber-600' },
    { key: 'published', color: 'text-emerald-600' },
    { key: 'sold_bm', color: 'text-blue-600' },
    { key: 'sold_b2b', color: 'text-purple-600' },
    { key: 'ineligible', color: 'text-slate-500' },
  ];

  return (
    <DashboardLayout>
      <div data-testid="autopub-page" className="space-y-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Rocket className="w-6 h-6 text-emerald-600" /> Publication Auto
              {dryRun && (
                <Badge data-testid="dry-run-badge" className="bg-orange-100 text-orange-700 ml-2">
                  <AlertTriangle className="w-3 h-3 mr-1" /> Mode simulation
                </Badge>
              )}
            </h1>
            <p className="text-slate-500 mt-1">ERP → pricing → Back Market. Vous saisissez le <strong>prix minimum</strong>, le prix cible est fixé à +7%. SKU auto (m… TVA marge / 11… TVA normale), commission 12% + 20€ fixes.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button data-testid="erp-doc-btn" variant="outline" onClick={() => setDocOpen(true)}>
              <BookOpen className="w-4 h-4 mr-2" /> Doc ERP
            </Button>
            <Button data-testid="reindex-btn" variant="outline" onClick={reindex} disabled={reindexing}>
              {reindexing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Database className="w-4 h-4 mr-2" />}
              Indexer catalogue BM {indexInfo ? `(${indexInfo.index_count})` : ''}
            </Button>
            <Button data-testid="refresh-buybox-btn" variant="outline" onClick={refreshBuybox} disabled={refreshingBuybox}>
              {refreshingBuybox ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Crosshair className="w-4 h-4 mr-2" />}
              Backbox
            </Button>
            <Button data-testid="run-watch-btn" variant="outline" onClick={runWatch}>
              <RefreshCcw className="w-4 h-4 mr-2" /> Vérifier ventes
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {statCards.map(({ key, color }) => (
            <Card key={key} data-testid={`stat-${key}`} className={`cursor-pointer transition-shadow hover:shadow-md ${filter === key ? 'ring-2 ring-emerald-500' : ''}`}
              onClick={() => setFilter(filter === key ? null : key)}>
              <CardContent className="p-4">
                <p className={`text-2xl font-bold ${color}`}>{counts[key] || 0}</p>
                <p className="text-xs text-slate-500 mt-1">{STATUS_META[key].label}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card>
          <CardContent className="p-0 overflow-x-auto">
            {loading ? (
              <div className="p-12 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
            ) : items.length === 0 ? (
              <div data-testid="empty-board" className="p-12 text-center text-slate-400">
                <Rocket className="w-10 h-10 mx-auto mb-3 opacity-40" />
                <p>Aucun produit{filter ? ' pour ce filtre' : ''}. Votre ERP peut pousser des produits via l'API (voir Doc ERP).</p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-xs text-slate-500 uppercase">
                    <th className="px-4 py-3">Produit</th>
                    <th className="px-3 py-3">Grade / Batt.</th>
                    <th className="px-3 py-3">TVA</th>
                    <th className="px-3 py-3">Coût total</th>
                    <th className="px-3 py-3">Backbox</th>
                    <th className="px-3 py-3">Prix minimum</th>
                    <th className="px-3 py-3">Commission 12%</th>
                    <th className="px-3 py-3">TVA à déduire</th>
                    <th className="px-3 py-3">Marge nette</th>
                    <th className="px-3 py-3">Statut</th>
                    <th className="px-3 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => {
                    const d = getDraft(it);
                    const sim = computeSim(d.sale_price, d.cost_total, d.tva_regime);
                    const editable = ['pending_price', 'publish_error'].includes(it.status);
                    return (
                      <tr key={it.id} data-testid={`autopub-row-${it.sku}`} className={`border-b border-slate-100 ${!it.eligible ? 'opacity-60 bg-slate-50' : ''}`}>
                        <td className="px-4 py-3">
                          <p className="font-medium text-slate-900">{it.title}</p>
                          <p className="text-xs text-slate-500">IMEI {it.imei} · SKU <span className="font-mono">{it.sku}</span></p>
                          {it.matched_title && <p className="text-xs text-emerald-600">↳ BM : {it.matched_title}</p>}
                          {!it.eligible && it.eligibility_reasons?.length > 0 && (
                            <p className="text-xs text-red-500 mt-1">{it.eligibility_reasons.join(' · ')}</p>
                          )}
                          {it.status === 'publish_error' && it.bm_import_response?.body && (
                            <p className="text-xs text-red-500 mt-1">BM : {String(it.bm_import_response.body).slice(0, 120)}</p>
                          )}
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap">
                          <Badge variant="outline">{it.grade || '?'}</Badge>
                          <span className="ml-1 text-xs text-slate-500">{it.battery_health != null ? `${it.battery_health}%` : ''}</span>
                          {it.bm_state && <p className="text-xs text-slate-400 mt-1">{BM_STATES[it.bm_state]}</p>}
                        </td>
                        <td className="px-3 py-3">
                          {editable ? (
                            <select data-testid={`tva-select-${it.sku}`} value={d.tva_regime}
                              onChange={(e) => setDraft(it.id, { tva_regime: e.target.value })}
                              className="border border-slate-200 rounded-md px-2 py-1 text-sm bg-white">
                              <option value="marge">Marge</option>
                              <option value="normale">Normale</option>
                            </select>
                          ) : <span className="capitalize">{it.tva_regime}</span>}
                        </td>
                        <td className="px-3 py-3 w-28">
                          {editable ? (
                            <Input data-testid={`cost-input-${it.sku}`} type="number" step="0.01" value={d.cost_total}
                              onChange={(e) => setDraft(it.id, { cost_total: e.target.value })} className="h-8 w-24" />
                          ) : eur(it.cost_total)}
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap">
                          {it.buybox ? (
                            <div data-testid={`buybox-${it.sku}`}>
                              <span className="font-medium text-indigo-600">{eur(it.buybox.price_for_buybox)}</span>
                              {it.buybox.buybox && <Badge className="ml-1 bg-indigo-100 text-indigo-700 text-[10px] px-1.5">Gagnée</Badge>}
                            </div>
                          ) : <span className="text-slate-300">—</span>}
                        </td>
                        <td className="px-3 py-3 w-28">
                          {editable ? (
                            <>
                              <Input data-testid={`price-input-${it.sku}`} type="number" step="0.01" value={d.sale_price} placeholder="0.00"
                                onChange={(e) => setDraft(it.id, { sale_price: e.target.value })} className="h-8 w-24 font-medium" />
                              {parseFloat(d.sale_price) > 0 && (
                                <p className="text-xs text-slate-400 mt-1">Cible : {eur(parseFloat(d.sale_price) * 1.07)}</p>
                              )}
                            </>
                          ) : (
                            <>
                              <span className="font-medium">{eur(it.sale_price)}</span>
                              {it.target_price && <p className="text-xs text-slate-400">Cible : {eur(it.target_price)}</p>}
                            </>
                          )}
                        </td>
                        <td className="px-3 py-3 text-slate-600">{sim ? eur(sim.commission + 20) : '—'}
                          {sim && <p className="text-xs text-slate-400">{eur(sim.commission)} + 20€</p>}
                        </td>
                        <td className="px-3 py-3 text-slate-600">{sim ? eur(sim.tva) : '—'}</td>
                        <td className="px-3 py-3">
                          {sim ? (
                            <span data-testid={`margin-${it.sku}`} className={`font-bold ${sim.net >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{eur(sim.net)}</span>
                          ) : '—'}
                        </td>
                        <td className="px-3 py-3">
                          <Badge data-testid={`status-${it.sku}`} className={STATUS_META[it.status]?.cls || ''}>
                            {STATUS_META[it.status]?.label || it.status}
                          </Badge>
                          {it.dry_run && it.status === 'published' && <p className="text-xs text-orange-500 mt-1">simulation</p>}
                          {it.status === 'sold_b2b' && !it.bm_removed && !it.dry_run && (
                            <p data-testid={`bm-removal-pending-${it.sku}`} className="text-xs text-red-600 font-medium mt-1">⚠️ Encore en vente BM — retrait auto en cours</p>
                          )}
                          {it.status === 'published' && it.bm_relist_pending && !it.dry_run && (
                            <p data-testid={`bm-relist-pending-${it.sku}`} className="text-xs text-amber-600 font-medium mt-1">⚠️ Remise en vente BM en attente — réessai auto</p>
                          )}
                          {it.order_id && <p className="text-xs text-slate-400 mt-1">Cmd {it.order_id}</p>}
                        </td>
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-1">
                            {editable && (
                              <>
                                <Button data-testid={`publish-btn-${it.sku}`} size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-8"
                                  disabled={busyId === it.id} onClick={() => publish(it)}>
                                  {busyId === it.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Rocket className="w-3 h-3 mr-1" />} Publier
                                </Button>
                                <Button data-testid={`save-btn-${it.sku}`} size="sm" variant="outline" className="h-8"
                                  disabled={busyId === it.id} onClick={() => saveItem(it)}>Enregistrer</Button>
                                <Button data-testid={`match-btn-${it.sku}`} size="sm" variant="ghost" className="h-8" title="Associer manuellement au catalogue BM"
                                  onClick={() => { setMatchItem(it); setMatchQuery(it.title); searchMatch(it.title); }}>
                                  <Search className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                            {(it.status === 'published' || (it.status === 'sold_b2b' && !it.bm_removed && !it.dry_run)) && (
                              <Button data-testid={`remove-bm-btn-${it.sku}`} size="sm" variant="outline" className="h-8 text-red-600"
                                disabled={busyId === it.id} onClick={() => removeFromBm(it)} title="Retirer l'annonce de BM (quantité 0)">
                                <EyeOff className="w-4 h-4" />
                              </Button>
                            )}
                            <Button data-testid={`delete-btn-${it.sku}`} size="sm" variant="ghost" className="h-8 text-slate-400 hover:text-red-600"
                              disabled={busyId === it.id} onClick={() => deleteItem(it)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        {/* Doc ERP */}
        <Dialog open={docOpen} onOpenChange={setDocOpen}>
          <DialogContent data-testid="erp-doc-modal" className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Intégration ERP — Auto-publication</DialogTitle>
              <DialogDescription>Configurez votre ERP pour pousser les produits vers RefurbFlow (auth : header <code>X-API-Key</code>, clé générée dans Paramètres).</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 text-sm">
              <div>
                <p className="font-semibold mb-1">1. Produit prêt à vendre B2B</p>
                <pre className="bg-slate-900 text-slate-100 rounded-lg p-3 text-xs overflow-x-auto">{`POST ${process.env.REACT_APP_BACKEND_URL}/api/erp/products/push
X-API-Key: <votre clé API>

{
  "imei": "353912345678901",
  "status": "pret_a_vendre_b2b",
  "title": "iPhone 13 128 Go Noir",
  "grade": "A",              // A→Comme neuf, B→Très bon, C→Bon, D→refusé
  "battery_health": 92,      // doit être > 85
  "functional_state": "full_ok",
  "defects": [],             // face_id / nfc => exclu
  "tva_regime": "marge",     // "marge" (SKU m…) ou "normale" (SKU 11…)
  "cost_total": 320.50
}`}</pre>
              </div>
              <div>
                <p className="font-semibold mb-1">2. Téléphone vendu en B2B (retire l'annonce BM)</p>
                <pre className="bg-slate-900 text-slate-100 rounded-lg p-3 text-xs overflow-x-auto">{`POST ${process.env.REACT_APP_BACKEND_URL}/api/erp/products/push
X-API-Key: <votre clé API>

{ "imei": "353912345678901", "status": "vendu_b2b" }

// Réservation annulée ? Renvoyez le statut "pret_a_vendre_b2b"
// avec le même IMEI : l'annonce BM est remise en vente (quantité 1).`}</pre>
              </div>
              <div>
                <p className="font-semibold mb-1">3. Envoi en masse</p>
                <pre className="bg-slate-900 text-slate-100 rounded-lg p-3 text-xs overflow-x-auto">{`POST ${process.env.REACT_APP_BACKEND_URL}/api/erp/products/push/batch
{ "items": [ { ... }, { ... } ] }`}</pre>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-blue-800 text-xs">
                <p className="font-semibold">Vente sur Back Market → ERP (quasi temps réel)</p>
                <p>Toutes les 2 minutes, RefurbFlow vérifie les commandes BM. Si un SKU auto-publié est vendu, votre webhook ERP (Paramètres) reçoit immédiatement :</p>
                <pre className="mt-1">{`{ "imei": "…", "sku": "m…", "order_id": 123, "status": "vendu-bm" }`}</pre>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Match manuel */}
        <Dialog open={!!matchItem} onOpenChange={(o) => !o && setMatchItem(null)}>
          <DialogContent data-testid="match-modal" className="max-w-xl">
            <DialogHeader>
              <DialogTitle>Associer au catalogue Back Market</DialogTitle>
              <DialogDescription>{matchItem?.title} — recherchez le produit BM correspondant</DialogDescription>
            </DialogHeader>
            <Input data-testid="match-search-input" value={matchQuery} onChange={(e) => searchMatch(e.target.value)} placeholder="iPhone 13 128 Go Noir…" />
            <div className="max-h-64 overflow-y-auto space-y-1">
              {matchResults.map((r) => (
                <button key={r.backmarket_id} data-testid={`match-result-${r.backmarket_id}`}
                  className="w-full text-left px-3 py-2 rounded-lg hover:bg-emerald-50 border border-slate-100 text-sm"
                  onClick={() => selectMatch(r)}>
                  {r.title} <span className="text-xs text-slate-400">#{r.backmarket_id}</span>
                </button>
              ))}
              {matchQuery.length >= 3 && matchResults.length === 0 && (
                <p className="text-sm text-slate-400 p-3">Aucun résultat — indexez le catalogue BM d'abord.</p>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
